// RolesPage — wired to real backend (S13, Agent U).
//
// BE supports: list roles (with permissions), get role, create role, update
// role permissions, delete (non-system) role. Permission editing is driven
// from the BE-returned `permissions[]` array on each role; the UI presents a
// checkbox grid grouped by domain (parsed from the `domain.action` permission
// keys returned by the BE).

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { Pencil, Plus, ShieldCheck, ShieldX, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { rolesApi, type RoleDto } from '@api/roles';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';
import { toast } from 'sonner';
import type { Translator } from '@/i18n/config';

const { Text } = Typography;

// BE permission prefixes that have a dedicated TR group label.
const GROUP_PREFIXES = new Set([
  'content',
  'taxonomy',
  'page',
  'web',
  'users',
  'user',
  'kvkk',
  'roles',
  'role',
  'audit',
  'settings',
]);

function groupOf(permissionKey: string, t: Translator): string {
  const prefix = permissionKey.split('.')[0] ?? 'other';
  if (GROUP_PREFIXES.has(prefix)) return t(`roles.groups.${prefix}`);
  return prefix.charAt(0).toUpperCase() + prefix.slice(1);
}

function allPermissionsFrom(roles: RoleDto[]): string[] {
  const set = new Set<string>();
  for (const r of roles) for (const p of r.permissions) set.add(p);
  return Array.from(set).sort();
}

export function RolesPage() {
  const qc = useQueryClient();
  const { t } = useTranslation(['users', 'common']);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm<{ name: string; description?: string }>();

  // ISSUE-50 — rename modal
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<RoleDto | null>(null);
  const [renameForm] = Form.useForm<{ name: string; description?: string }>();

  const { data: roles = [], isFetching } = useQuery({
    queryKey: ['roles', { includeSystem: true }],
    queryFn: () => rolesApi.list({ includeSystem: true }),
  });

  // Canonical permission universe from the backend catalog — the source of
  // truth for what the editor can offer. Falls back to the union of existing
  // roles only if the catalog endpoint is unavailable.
  const { data: catalog } = useQuery({
    queryKey: ['permission-catalog'],
    queryFn: () => rolesApi.catalog(),
    staleTime: 5 * 60_000,
  });

  // ISSUE-51 — fetch authoritative permissions from dedicated endpoint for the
  // selected role, instead of re-deriving from the role list payload.
  const { data: selectedRolePerms } = useQuery({
    queryKey: ['role-permissions', selectedRoleId],
    queryFn: () => rolesApi.getPermissions(selectedRoleId ?? ''),
    enabled: !!selectedRoleId,
  });

  const allPerms = useMemo(
    () => (catalog && catalog.length > 0 ? [...catalog].sort() : allPermissionsFrom(roles)),
    [catalog, roles],
  );
  const grouped = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const p of allPerms) {
      const g = groupOf(p, t as unknown as Translator);
      const arr = map.get(g) ?? [];
      arr.push(p);
      map.set(g, arr);
    }
    return Array.from(map.entries()).sort((a, b) =>
      a[0].localeCompare(b[0], 'tr'),
    );
  }, [allPerms, t]);

  const selectedRole = useMemo(
    () => roles.find((r) => r.id === selectedRoleId) ?? null,
    [roles, selectedRoleId],
  );

  // Local pending-permissions buffer for the right-hand editor.
  const [pendingPerms, setPendingPerms] = useState<Set<string>>(new Set());
  const [draftFor, setDraftFor] = useState<string | null>(null);

  // Sync the draft whenever a different role is selected. Prefer the dedicated
  // /permissions endpoint payload when available; fall back to the role list.
  if (selectedRole && draftFor !== selectedRole.id) {
    setDraftFor(selectedRole.id);
    setPendingPerms(new Set(selectedRolePerms ?? selectedRole.permissions));
  }

  const togglePerm = (p: string, on: boolean) => {
    setPendingPerms((prev) => {
      const next = new Set(prev);
      if (on) next.add(p);
      else next.delete(p);
      return next;
    });
  };

  const updatePermsMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRole) throw new Error('no role selected');
      await rolesApi.updatePermissions(selectedRole.id, {
        permissions: Array.from(pendingPerms),
      });
    },
    onSuccess: async () => {
      toast.success(t('roles.toast.permsUpdated'), {
        description: t('roles.toast.permsUpdatedHint'),
      });
      await qc.invalidateQueries({ queryKey: ['roles'] });
      void recordAuditAction({
        action: 'role.permissions.update',
        resource: 'role',
        resourceId: selectedRole?.id,
        summary: `${selectedRole?.name} rolü yetkileri güncellendi`,
        meta: { count: pendingPerms.size },
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const createRoleMutation = useMutation({
    mutationFn: async (values: { name: string; description?: string }) => {
      return rolesApi.create({
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        permissions: [],
      });
    },
    onSuccess: async (id, vars) => {
      toast.success(t('roles.toast.created', { name: vars.name }));
      await qc.invalidateQueries({ queryKey: ['roles'] });
      void recordAuditAction({
        action: 'role.create',
        resource: 'role',
        resourceId: id,
        summary: `${vars.name} rolü oluşturuldu`,
      });
      setCreateOpen(false);
      createForm.resetFields();
      setSelectedRoleId(id);
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  // ISSUE-50 — rename mutation
  const renameRoleMutation = useMutation({
    mutationFn: async (values: { name: string; description?: string }) => {
      if (!renameTarget) throw new Error('no target');
      return rolesApi.update(renameTarget.id, {
        name: values.name.trim(),
        description: values.description?.trim() ?? null,
      });
    },
    onSuccess: async (updated) => {
      toast.success(t('roles.toast.renamed', { name: updated.name }));
      await qc.invalidateQueries({ queryKey: ['roles'] });
      void recordAuditAction({
        action: 'role.update',
        resource: 'role',
        resourceId: updated.id,
        summary: `${updated.name} rolü güncellendi`,
      });
      setRenameOpen(false);
      setRenameTarget(null);
      renameForm.resetFields();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (role: RoleDto) => rolesApi.remove(role.id),
    onSuccess: async (_d, role) => {
      toast.success(t('roles.toast.deleted', { name: role.name }));
      await qc.invalidateQueries({ queryKey: ['roles'] });
      void recordAuditAction({
        action: 'role.delete',
        resource: 'role',
        resourceId: role.id,
        summary: `${role.name} rolü silindi`,
      });
      if (selectedRoleId === role.id) setSelectedRoleId(null);
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const dirty = useMemo(() => {
    if (!selectedRole) return false;
    if (pendingPerms.size !== selectedRole.permissions.length) return true;
    for (const p of selectedRole.permissions) if (!pendingPerms.has(p)) return true;
    return false;
  }, [pendingPerms, selectedRole]);

  return (
    <div>
      <PageHeader
        title={t('roles.page.title')}
        description={t('roles.page.description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('roles.breadcrumb.users') },
          { label: t('roles.breadcrumb.list') },
        ]}
        actions={
          <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => setCreateOpen(true)}
          >
            {t('roles.actions.new')}
          </Button>
        }
      />

      <div className="roles-grid" style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(220px, 1fr) minmax(0, 2fr)' }}>
        <Card title={t('roles.listCard.title')} size="small">
          {isFetching && roles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <Spin />
            </div>
          ) : roles.length === 0 ? (
            <Text type="secondary">{t('roles.listCard.empty')}</Text>
          ) : (
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {roles.map((r) => (
                <div
                  key={r.id}
                  data-testid={`role-row-${r.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedRoleId(r.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedRoleId(r.id);
                    }
                  }}
                  style={{
                    cursor: 'pointer',
                    padding: 10,
                    borderRadius: 8,
                    border: '1px solid var(--ds-border)',
                    background:
                      r.id === selectedRoleId
                        ? 'var(--ds-bg-muted)'
                        : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--ds-text-secondary)' }}>
                        {t('roles.listCard.meta', { permCount: r.permissions.length, userCount: r.userCount })}
                      </div>
                    </div>
                    {r.isSystem ? (
                      <Tag color="blue">{t('roles.listCard.system')}</Tag>
                    ) : (
                      <Space size={2}>
                        <Button
                          size="small"
                          type="text"
                          icon={<Pencil size={12} />}
                          aria-label={t('roles.listCard.renameAria', { name: r.name })}
                          onClick={(e) => {
                            e.stopPropagation();
                            setRenameTarget(r);
                            renameForm.setFieldsValue({
                              name: r.name,
                              description: r.description ?? '',
                            });
                            setRenameOpen(true);
                          }}
                        />
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<Trash2 size={12} />}
                          aria-label={t('roles.listCard.deleteAria', { name: r.name })}
                          onClick={(e) => {
                            e.stopPropagation();
                            Modal.confirm({
                              title: t('roles.delete.title', { name: r.name }),
                              content: t('roles.delete.content'),
                              okText: t('roles.delete.ok'),
                              okButtonProps: { danger: true },
                              cancelText: t('roles.delete.cancel'),
                              onOk: () => deleteRoleMutation.mutate(r),
                            });
                          }}
                        />
                      </Space>
                    )}
                  </div>
                </div>
              ))}
            </Space>
          )}
        </Card>

        <Card
          title={
            selectedRole ? (
              <Space>
                <span>{selectedRole.name}</span>
                {selectedRole.isSystem ? <Tag color="blue">{t('roles.editorCard.system')}</Tag> : null}
              </Space>
            ) : (
              t('roles.editorCard.selectPrompt')
            )
          }
          size="small"
          extra={
            selectedRole && !selectedRole.isSystem ? (
              <Button
                type="primary"
                size="small"
                disabled={!dirty}
                loading={updatePermsMutation.isPending}
                onClick={() => updatePermsMutation.mutate()}
              >
                {t('roles.editorCard.save', { count: pendingPerms.size })}
              </Button>
            ) : null
          }
        >
          {!selectedRole ? (
            <Alert
              type="info"
              showIcon
              message={t('roles.editorCard.noSelection')}
            />
          ) : selectedRole.isSystem ? (
            <>
              <Alert
                type="info"
                showIcon
                message={t('roles.editorCard.systemReadonly')}
                style={{ marginBottom: 12 }}
              />
              {grouped.map(([group, perms]) => (
                <div key={group} style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 12,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      color: 'var(--ds-text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    {group}
                  </div>
                  <Space wrap>
                    {perms.map((p) =>
                      selectedRole.permissions.includes(p) ? (
                        <Tag
                          key={p}
                          color="green"
                          icon={<ShieldCheck size={12} />}
                        >
                          {p}
                        </Tag>
                      ) : (
                        <Tag key={p} icon={<ShieldX size={12} />}>
                          {p}
                        </Tag>
                      ),
                    )}
                  </Space>
                </div>
              ))}
            </>
          ) : (
            <>
              {grouped.map(([group, perms]) => (
                <div key={group} style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 12,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      color: 'var(--ds-text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    {group}
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                      gap: 6,
                    }}
                  >
                    {perms.map((p) => (
                      <Checkbox
                        key={p}
                        checked={pendingPerms.has(p)}
                        onChange={(e) => togglePerm(p, e.target.checked)}
                      >
                        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {p}
                        </span>
                      </Checkbox>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </Card>
      </div>

      <Modal
        title={t('roles.createModal.title')}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        okText={t('roles.createModal.ok')}
        confirmLoading={createRoleMutation.isPending}
        onOk={() => {
          createForm
            .validateFields()
            .then((v) => createRoleMutation.mutate(v))
            .catch(() => undefined);
        }}
      >
        <Form layout="vertical" form={createForm}>
          <Form.Item
            label={t('roles.form.name')}
            name="name"
            rules={[
              { required: true, message: t('roles.form.nameRequired') },
              { min: 2, max: 50, message: t('roles.form.nameLength') },
            ]}
          >
            {/* Modal, kullanıcı aksiyonuyla açılır; ilk alana odak vermek bu
                bağlamda erişilebilirlik SORUNU değil, beklenen davranıştır. */}
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <Input placeholder={t('roles.createModal.namePlaceholder')} autoFocus />
          </Form.Item>
          <Form.Item label={t('roles.form.description')} name="description">
            <Input.TextArea rows={2} maxLength={200} showCount />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('roles.createModal.hint')}
          </Text>
        </Form>
      </Modal>

      {/* ISSUE-50 — rename modal */}
      <Modal
        title={t('roles.renameModal.title', { name: renameTarget?.name ?? '' })}
        open={renameOpen}
        onCancel={() => {
          setRenameOpen(false);
          setRenameTarget(null);
        }}
        okText={t('roles.renameModal.ok')}
        cancelText={t('roles.renameModal.cancel')}
        confirmLoading={renameRoleMutation.isPending}
        onOk={() => {
          renameForm
            .validateFields()
            .then((v) => renameRoleMutation.mutate(v))
            .catch(() => undefined);
        }}
      >
        <Form layout="vertical" form={renameForm}>
          <Form.Item
            label={t('roles.form.name')}
            name="name"
            rules={[
              { required: true, message: t('roles.form.nameRequired') },
              { min: 2, max: 50, message: t('roles.form.nameLength') },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item label={t('roles.form.description')} name="description">
            <Input.TextArea rows={2} maxLength={200} showCount />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('roles.renameModal.hint')}
          </Text>
        </Form>
      </Modal>
    </div>
  );
}
